import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Drawer, Input, Spin, Badge, Tag } from 'antd'
import {
	SendOutlined,
	FileSearchOutlined,
	HistoryOutlined,
	CheckCircleOutlined,
	WarningOutlined,
	LoadingOutlined,
	StopOutlined,
} from '@ant-design/icons'
import Showdown from 'showdown'
import { bool, func, string, oneOfType, number } from 'prop-types'

import api from '../../services/api'
import { errorMessage } from '~/services/messager'
import assistant_icon from '../../assets/icons/assistant_icon.svg'
import user_icon from '../../assets/icons/user_icon.svg'

import {
	AssistantMessage,
	HistoryContainer,
	HistoryEmpty,
	InputContainer,
	LimitScreen,
	Messages,
	SendButton,
	StartReviewButton,
	StartReviewContainer,
	TabBar,
	TabItem,
	TipCard,
	UserMessage,
} from './styles'

const REVIEW_LABEL = 'Revisar o documento completo'

const buildInitialPrompt = (text) =>
	`Sou do setor jurídico e preciso de uma revisão completa do seguinte contrato/documento. Analise o texto abaixo e identifique: erros, inconsistências jurídicas, cláusulas problemáticas, termos ambíguos, obrigações desproporcionais ou qualquer ponto que mereça atenção antes da assinatura.

--- DOCUMENTO ---
${text}
--- FIM DO DOCUMENTO ---

Apresente os pontos encontrados de forma clara e organizada, agrupando por categoria (ex: erros formais, riscos jurídicos, cláusulas problemáticas), com destaque para os itens mais críticos.`

const stripHtml = (html) => {
	if (!html) return ''
	return html
		.replace(/<br\s*\/?>/gi, '\n')
		.replace(/<\/p>/gi, '\n')
		.replace(/<\/div>/gi, '\n')
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/\n{3,}/g, '\n\n')
		.trim()
}

const storageKey = (docId) => `doc_review_tips_${docId}`

const loadTips = (docId) => {
	try {
		const raw = localStorage.getItem(storageKey(docId))
		return raw ? JSON.parse(raw) : []
	} catch {
		return []
	}
}

const saveTips = (docId, tips) => {
	try {
		localStorage.setItem(storageKey(docId), JSON.stringify(tips))
	} catch {}
}

const formatDate = (iso) => {
	const d = new Date(iso)
	return d.toLocaleString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

const DocumentReviewer = ({ open, onClose, docId, docTitle, docText }) => {
	const converter = new Showdown.Converter()
	const isMobile = useMediaQuery({ query: '(max-width: 768px)' })

	const [activeTab, setActiveTab] = useState('review')
	const [messages, setMessages] = useState([])
	const [tips, setTips] = useState([])
	const [loading, setLoading] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [threadId, setThreadId] = useState(null)
	const [reviewStarted, setReviewStarted] = useState(false)

	const [pdfText, setPdfText] = useState('')
	const [extractingPdf, setExtractingPdf] = useState(false)

	// Usage limit state
	const [usageInfo, setUsageInfo] = useState(null)
	const [checkingUsage, setCheckingUsage] = useState(false)

	const messagesEndRef = useRef(null)

	const editorText = stripHtml(docText)
	const reviewText = editorText.length > 10 ? editorText : pdfText
	const hasReviewText = reviewText.length > 10
	const isPdfDoc = !editorText || editorText.length <= 10
	const limitReached = usageInfo && usageInfo.remaining === 0

	useEffect(() => {
		if (open && docId) {
			setTips(loadTips(docId))
		}
	}, [open, docId])

	useEffect(() => {
		if (open) {
			setMessages([])
			setInputValue('')
			setThreadId(null)
			setReviewStarted(false)
			setActiveTab('review')
			setPdfText('')
			setUsageInfo(null)
			fetchUsage()
		}
	}, [open, docId])

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}, [messages])

	const fetchUsage = () => {
		setCheckingUsage(true)
		api
			.get('/ai/document-review/usage')
			.then((res) => {
				setCheckingUsage(false)
				setUsageInfo(res.data)
			})
			.catch(() => {
				setCheckingUsage(false)
				// If check fails, allow usage (fail open)
				setUsageInfo(null)
			})
	}

	const persistTip = useCallback(
		(content) => {
			const tip = { date: new Date().toISOString(), content }
			setTips((prev) => {
				const updated = [tip, ...prev]
				saveTips(docId, updated)
				return updated
			})
		},
		[docId]
	)

	const sendToApi = useCallback(
		(nextMessages) => {
			const payload = {
				messages: nextMessages,
				document_id: docId,
				...(threadId && { thread_id: threadId }),
			}
			setLoading(true)
			api
				.post('/ai/document-review/', payload)
				.then((response) => {
					setLoading(false)
					const { answer, thread_id } = response.data
					if (thread_id && thread_id !== threadId) setThreadId(thread_id)
					setMessages([...nextMessages, { role: 'assistant', content: answer }])
					persistTip(answer)
					setUsageInfo((prev) =>
						prev
							? {
									...prev,
									used: prev.used + 1,
									remaining: Math.max(0, prev.remaining - 1),
								}
							: prev
					)
				})
				.catch((err) => {
					setLoading(false)
					if (err?.response?.status === 429) {
						setUsageInfo({ ...err.response.data })
					} else {
						errorMessage({
							content:
								'Houve um erro na comunicação com o assistente. Tente novamente.',
						})
					}
				})
		},
		[docId, threadId, persistTip]
	)

	const startReview = () => {
		setReviewStarted(true)
		const userContent = buildInitialPrompt(reviewText)
		const initial = [{ role: 'user', content: userContent }]
		setMessages(initial)

		setLoading(true)
		api
			.post('/ai/document-review/', {
				messages: initial,
				document_id: docId,
			})
			.then((response) => {
				setLoading(false)
				const { answer, thread_id } = response.data
				if (thread_id) setThreadId(thread_id)
				setMessages([...initial, { role: 'assistant', content: answer }])
				persistTip(answer)
				// Decrement remaining count locally to avoid extra fetch
				setUsageInfo((prev) =>
					prev
						? { ...prev, used: prev.used + 1, remaining: prev.remaining - 1 }
						: prev
				)
			})
			.catch((err) => {
				setLoading(false)
				if (err?.response?.status === 429) {
					const data = err.response.data
					setUsageInfo({ ...data })
					setReviewStarted(false)
					setMessages([])
				} else {
					errorMessage({
						content:
							'Houve um erro na comunicação com o assistente. Tente novamente.',
					})
					setReviewStarted(false)
					setMessages([])
				}
			})
	}

	const sendMessage = () => {
		if (!inputValue.trim() || loading) return
		const next = [...messages, { role: 'user', content: inputValue.trim() }]
		setMessages(next)
		setInputValue('')
		sendToApi(next)
	}

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			sendMessage()
		}
	}

	const extractPdfText = () => {
		setExtractingPdf(true)
		api
			.get(`/documents/${docId}/text-extract`)
			.then((response) => {
				setExtractingPdf(false)
				const text = response.data.text || ''
				if (text.trim().length === 0) {
					errorMessage({
						content:
							'Não foi possível extrair texto deste PDF. O documento pode ser uma imagem escaneada.',
					})
				} else {
					setPdfText(text)
				}
			})
			.catch(() => {
				setExtractingPdf(false)
				errorMessage({
					content: 'Erro ao extrair texto do PDF. Tente novamente.',
				})
			})
	}

	const isInitialReviewMessage = (msg) =>
		msg.role === 'user' && msg.content.startsWith('Sou do setor jurídico')

	const drawerTitle = (
		<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
			<FileSearchOutlined style={{ color: '#002880' }} />
			Revisor de Contratos
			{docTitle ? (
				<span
					style={{
						fontSize: '0.75rem',
						color: '#888',
						fontWeight: 400,
						maxWidth: 200,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}>
					— {docTitle}
				</span>
			) : null}
		</span>
	)

	const renderDocumentStatus = () => {
		if (hasReviewText) {
			const source = editorText.length > 10 ? 'editor' : 'PDF'
			return (
				<Tag
					icon={<CheckCircleOutlined />}
					color="success"
					style={{ fontSize: '0.8rem' }}>
					Texto carregado via {source} (
					{reviewText.length.toLocaleString('pt-BR')} caracteres)
				</Tag>
			)
		}
		if (isPdfDoc) {
			if (extractingPdf) {
				return (
					<Tag
						icon={<LoadingOutlined spin />}
						color="processing"
						style={{ fontSize: '0.8rem' }}>
						Extraindo texto do PDF...
					</Tag>
				)
			}
			return (
				<StartReviewButton
					onClick={extractPdfText}
					icon={<FileSearchOutlined />}
					style={{
						fontSize: '0.85rem',
						height: '2.5rem',
						padding: '0 1.5rem',
					}}>
					Carregar texto do PDF
				</StartReviewButton>
			)
		}
		return (
			<Tag
				icon={<WarningOutlined />}
				color="warning"
				style={{ fontSize: '0.8rem' }}>
				Documento sem conteúdo de texto disponível
			</Tag>
		)
	}

	const renderUsageBadge = () => {
		if (!usageInfo) return null
		const { used, limit } = usageInfo
		return (
			<span style={{ fontSize: '0.75rem', color: '#888' }}>
				{used}/{limit} revisões usadas este mês
			</span>
		)
	}

	return (
		<Drawer
			title={drawerTitle}
			placement="right"
			open={open}
			zIndex={50}
			onClose={onClose}
			width={isMobile ? '100%' : '55%'}
			bodyStyle={{
				padding: '12px 16px',
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
			}}>
			<TabBar>
				<TabItem
					$active={activeTab === 'review'}
					onClick={() => setActiveTab('review')}>
					Revisão
				</TabItem>
				<TabItem
					$active={activeTab === 'history'}
					onClick={() => setActiveTab('history')}>
					<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						<HistoryOutlined />
						Histórico de Dicas
						{tips.length > 0 && (
							<Badge
								count={tips.length}
								style={{ backgroundColor: '#002880' }}
								size="small"
							/>
						)}
					</span>
				</TabItem>
			</TabBar>

			{activeTab === 'review' && (
				<>
					{checkingUsage ? (
						<StartReviewContainer>
							<Spin size="large" />
							<p style={{ color: '#888' }}>Verificando disponibilidade...</p>
						</StartReviewContainer>
					) : limitReached ? (
						<LimitScreen>
							<StopOutlined
								style={{ fontSize: 52, color: '#c0392b', opacity: 0.8 }}
							/>
							<h3>Limite mensal atingido</h3>
							<p>
								Sua empresa utilizou todas as{' '}
								<strong>{usageInfo.limit} revisões</strong> disponíveis para
								este mês.
							</p>
							<div className="usage-bar">
								<div style={{ width: '100%' }} />
							</div>
							<span className="usage-label">
								{usageInfo.used}/{usageInfo.limit} revisões utilizadas
							</span>
							<div className="reset-info">
								O limite será renovado em{' '}
								<strong>{usageInfo.reset_date}</strong>
							</div>
						</LimitScreen>
					) : !reviewStarted ? (
						<StartReviewContainer>
							<FileSearchOutlined
								style={{ fontSize: 48, color: '#002880', opacity: 0.7 }}
							/>
							<h3>Revisor de Contratos com IA</h3>
							<p>
								O assistente analisará o documento em busca de erros, cláusulas
								problemáticas, inconsistências jurídicas e outros pontos que
								necessitam atenção.
							</p>

							{renderDocumentStatus()}
							{renderUsageBadge()}

							{hasReviewText && (
								<StartReviewButton
									onClick={startReview}
									icon={<FileSearchOutlined />}>
									Iniciar Revisão
								</StartReviewButton>
							)}
						</StartReviewContainer>
					) : (
						<Messages>
							{messages.map((msg, i) =>
								msg.role === 'user' ? (
									<UserMessage key={i}>
										<img src={user_icon} alt="Usuário" />
										<span>
											{isInitialReviewMessage(msg) ? REVIEW_LABEL : msg.content}
										</span>
									</UserMessage>
								) : (
									<AssistantMessage key={i}>
										<img src={assistant_icon} alt="Assistente" />
										<span
											dangerouslySetInnerHTML={{
												__html: converter.makeHtml(msg.content),
											}}
										/>
									</AssistantMessage>
								)
							)}
							{loading && (
								<AssistantMessage>
									<img src={assistant_icon} alt="Assistente" />
									<span>
										<Spin size="small" />
									</span>
								</AssistantMessage>
							)}
							<div ref={messagesEndRef} />
						</Messages>
					)}

					{reviewStarted && (
						<>
							{limitReached && (
								<div
									style={{
										fontSize: '0.78rem',
										color: '#c0392b',
										background: '#fdf0ef',
										border: '1px solid #f5c6c2',
										borderRadius: '0.375rem',
										padding: '0.4rem 0.75rem',
										marginBottom: '0.4rem',
									}}>
									Limite mensal atingido. Novas perguntas estarão disponíveis em{' '}
									<strong>{usageInfo?.reset_date}</strong>.
								</div>
							)}
							<InputContainer>
								<Input
									value={inputValue}
									onChange={(e) => setInputValue(e.target.value)}
									onKeyDown={handleKeyDown}
									placeholder={
										limitReached
											? 'Limite de uso mensal atingido'
											: 'Faça uma pergunta sobre o documento...'
									}
									disabled={loading || limitReached}
									style={{ borderRadius: '0.375rem' }}
								/>
								<SendButton
									onClick={sendMessage}
									icon={<SendOutlined />}
									disabled={loading || limitReached || !inputValue.trim()}
								/>
							</InputContainer>
						</>
					)}
				</>
			)}

			{activeTab === 'history' && (
				<HistoryContainer>
					{tips.length === 0 ? (
						<HistoryEmpty>
							<HistoryOutlined style={{ fontSize: 40, opacity: 0.3 }} />
							<span>
								Nenhuma dica registrada ainda.
								<br />
								Inicie uma revisão para começar.
							</span>
						</HistoryEmpty>
					) : (
						tips.map((tip, i) => (
							<TipCard key={i}>
								<div className="tip-date">{formatDate(tip.date)}</div>
								<div
									className="tip-content"
									dangerouslySetInnerHTML={{
										__html: converter.makeHtml(tip.content),
									}}
								/>
							</TipCard>
						))
					)}
				</HistoryContainer>
			)}
		</Drawer>
	)
}

DocumentReviewer.propTypes = {
	open: bool.isRequired,
	onClose: func.isRequired,
	docId: oneOfType([string, number]),
	docTitle: string,
	docText: string,
}

export default DocumentReviewer
